/**
 * Ролевая модель (решение Р-04).
 *
 * Три роли. Все видят ВСЮ таблицу — строчной фильтрации «рекрутёр видит
 * только своих» нет. Роли различаются правами на изменение данных,
 * экспорт и доступ к паспортам.
 *
 * Этот файл — единственный источник истины о правах. Бэкенд использует его
 * в middleware, фронтенд — чтобы прятать недоступные действия. UI прячет,
 * API запрещает; источник истины — API.
 */

export const ROLES = ['admin', 'recruiter', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Администратор',
  recruiter: 'Рекрутёр',
  viewer: 'Наблюдатель',
};

/**
 * Полный перечень прав. Именование: <объект>.<действие>.
 */
export const PERMISSIONS = [
  // Грид и кандидаты
  'candidate.view',
  'candidate.create',
  'candidate.edit',
  'candidate.delete',
  'candidate.restore',
  'candidate.bulk_edit',
  'candidate.assign_responsible',

  // Паспорта и документы — отдельные права: это чувствительные ПДн,
  // видеть кандидата и видеть скан его паспорта — разные уровни доступа.
  'document.view',
  'document.download',
  'document.upload',
  'document.delete',
  'document.replace',

  // Чёрный список — отдельная база, а не статус кандидата.
  // Смотреть его должен каждый, кто заводит заявки: сверка происходит
  // в момент создания. Вносить и снимать — право отдельное: попадание
  // в список закрывает человеку работу, и делаться это должно осознанно.
  'blacklist.view',
  'blacklist.manage',

  // Данные наружу
  'data.import',
  'data.export',

  /*
    Копирование в буфер есть у всех — без него нельзя перенести телефон
    кандидата в мессенджер. Ограничен его объём: сколько строк уходит
    за одно нажатие (настраивается в разделе «Настройки»).

    Это право снимает предел. Отдельным правом, а не «раз администратор,
    значит можно»: предел существует ради персональных данных, и снимать
    его должен тот, кто за них отвечает, — а не всякий, кому однажды
    понадобится менять системные настройки.
  */
  'copy.unlimited',

  // Отчёты по всей базе. Отдельным правом, а не «раз видишь кандидатов —
  // видишь и сводку»: рекрутёр работает со своими людьми, а воронка,
  // выработка по рекрутёрам и разбивка по городам — это картина по всей
  // компании, и смотрит её тот, кто ею управляет.
  'report.view',

  // Справочники
  'dictionary.view',
  'dictionary.edit',
  // Создание города прямо из ячейки грида (Р-08, п.8)
  'dictionary.quick_create',

  // Представления грида
  'view.manage_own',
  'view.manage_shared',

  /*
    Листы — остальные вкладки рабочей книги (Р-22).

    Права здесь грубые: «вообще есть ли доступ к листам». Кто видит
    конкретный лист и кто правит его данные, записано у самого листа
    (`visibleToRoles`, `editableByRoles`) и у каждой его колонки, потому
    что листы заводятся из интерфейса — перечислить их в коде нельзя.

    `sheet.create` есть у рекрутёра: решение заказчика. Риск «три почти
    одинаковых листа» закрыт подсказкой о похожих листах при создании
    и владельцем у листа, а не запретом.

    `sheet.manage` — структура и права листа. Своим листом владелец
    распоряжается и без этого права: проверка на владельца в маршруте.
  */
  'sheet.view',
  'sheet.create',
  'sheet.edit_data',
  'sheet.manage',

  // Администрирование
  'user.view',
  'user.manage',
  'audit.view',
  'settings.manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Матрица прав. Соответствует таблице в разделе 7 ТЗ.
 */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  admin: PERMISSIONS,

  recruiter: [
    'candidate.view',
    'candidate.create',
    'candidate.edit',
    'candidate.delete',
    'candidate.restore',
    'candidate.bulk_edit',
    'candidate.assign_responsible',
    'document.view',
    'document.download',
    'document.upload',
    // Замена своего же скана. Полного `document.delete` у рекрутёра нет
    // и не будет: он не должен уметь стирать документы. Но исправить
    // собственную ошибку — перезалить криво снятый паспорт — обязан,
    // иначе за каждой опечаткой придётся идти к администратору.
    // Заменить чужую загрузку это право не позволяет (проверка в files.ts).
    'document.replace',
    /*
      Выгрузки у рекрутёра нет — ни файлом, ни списком номеров для рассылки.

      Раньше была, и вместе с ней открывались разделы «Импорт и выгрузка»
      и «Рассылка»: любой рекрутёр мог скачать базу целиком. Паспортные поля
      сервер из выгрузки вырезает, но ФИО, телефоны и города уезжали файлом
      без следа в чьей-либо памяти. Наблюдателю выгрузку закрыли ровно
      поэтому же — «выгрузка базы должна оставаться под контролем», —
      а рекрутёру оставили просто по недосмотру.

      Что осталось: копирование выделенного в буфер. Оно тоже вынос данных,
      но ограничено тем, что человек видит на экране, и каждое пишется
      в журнал с объёмом и составом. Сверх того ограничен объём одного
      копирования — `copy.unlimited` этого права здесь нет намеренно.
    */
    'dictionary.view',
    'dictionary.quick_create',
    'view.manage_own',
    // Рекрутёр и вносит в чёрный список, и сверяется с ним: он первым
    // узнаёт, что человек сбежал с объекта, и заводит заявки тоже он
    'blacklist.view',
    'blacklist.manage',
    // Листы: смотрит, правит данные и заводит свои. Структуру и права
    // чужого листа менять не может — только своего, по владельцу
    'sheet.view',
    'sheet.create',
    'sheet.edit_data',
  ],

  // Наблюдатель — руководитель, который смотрит воронку и отчёты.
  //
  // Нет экспорта: выгрузка базы кандидатов с паспортными данными должна
  // оставаться под контролем.
  // Нет доступа к документам: чтобы смотреть отчётность, сканы паспортов
  // не нужны, а каждый лишний человек с доступом к ним — это лишний риск
  // по персональным данным. Принцип минимизации доступа.
  viewer: [
    'candidate.view',
    'report.view',
    'dictionary.view',
    'view.manage_own',
    'blacklist.view',
    // Смотреть листы — да, но только те, где он перечислен в `visibleToRoles`.
    // Новый лист закрыт по умолчанию, так что само это право ничего
    // не открывает: оно лишь не запрещает раздел целиком
    'sheet.view',
  ],
};

export function hasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function hasAnyPermission(role: Role, permissions: readonly Permission[]): boolean {
  return permissions.some((p) => hasPermission(role, p));
}

export function hasAllPermissions(role: Role, permissions: readonly Permission[]): boolean {
  return permissions.every((p) => hasPermission(role, p));
}

/** Человекочитаемые названия прав — для экрана управления пользователями. */
export const PERMISSION_LABELS: Record<Permission, string> = {
  'candidate.view': 'Просмотр таблицы кандидатов',
  'candidate.create': 'Добавление кандидата',
  'blacklist.view': 'Просмотр чёрного списка',
  'blacklist.manage': 'Внесение и снятие с чёрного списка',
  'candidate.edit': 'Редактирование данных кандидата',
  'candidate.delete': 'Удаление кандидата',
  'candidate.restore': 'Восстановление из корзины',
  'candidate.bulk_edit': 'Массовые операции',
  'candidate.assign_responsible': 'Назначение ответственного',
  'document.view': 'Просмотр документов и паспортов',
  'document.download': 'Скачивание документов',
  'document.upload': 'Загрузка документов',
  'document.replace': 'Замена своего документа',
  'document.delete': 'Удаление документов',
  'data.import': 'Импорт данных',
  'data.export': 'Экспорт данных',
  'copy.unlimited': 'Копирование в буфер без ограничения по строкам',
  'report.view': 'Отчёты по всей базе',
  'dictionary.view': 'Просмотр справочников',
  'dictionary.edit': 'Редактирование справочников',
  'dictionary.quick_create': 'Быстрое создание значения справочника',
  'view.manage_own': 'Личные представления таблицы',
  'view.manage_shared': 'Общие представления таблицы',
  'sheet.view': 'Просмотр листов',
  'sheet.create': 'Создание листов',
  'sheet.edit_data': 'Правка данных на листах',
  'sheet.manage': 'Структура и права листов',
  'user.view': 'Просмотр пользователей',
  'user.manage': 'Управление пользователями',
  'audit.view': 'Просмотр журнала изменений',
  'settings.manage': 'Настройки системы',
};
